import { useIntl } from 'react-intl'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { isTourV2OfferType } from 'lib/offer/offerTypes'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { useOfferPriceWithMember } from 'hooks/Offers/useOfferPrice'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import useOfferTilePriceBlockExperiment from 'hooks/Experiments/useOfferTilePriceBlockExperiment'
import OfferTilePriceBlockSavingsLabel from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockSavingsLabel'
import OfferTilePriceBlockCaption from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'

interface MappedProps {
  canViewLuxPlusBenefits: boolean
}

interface Props extends MappedProps {
  offerType: App.OfferType
  offer?: App.AnyOffer
  duration?: string
  price: number
  memberPrice: number
  value?: number
  /** Value for the displayed dated price; control retains its existing cached value. */
  priceBlockValue?: number
  hotelNights?: number
  roomCount?: number
  shouldDisplayValue?: boolean
}

function HotelPriceContent({
  offerType,
  offer,
  duration,
  memberPrice,
  price,
  value,
  priceBlockValue,
  hotelNights,
  roomCount,
  shouldDisplayValue = false,
  canViewLuxPlusBenefits,
}: Props) {
  const intl = useIntl()
  const isTourV2 = isTourV2OfferType(offerType)

  const durationText = duration ? intl.formatMessage(
    {
      id: 'gGlv6dE9mEohN8XQVdJe',
      defaultMessage: '{duration} from',
    },
    { duration },
  ) : intl.formatMessage({
    id: 'EbkM0UyCjXc1yFTwGJbD',
    defaultMessage: 'From',
  })

  const {
    total: publicTotal,
    memberTotal = 0,
    saleUnit,
  } = useOfferPriceWithMember({
    value: undefined,
    saleUnit: 'total',
    duration: hotelNights,
    total: price,
    memberTotal: memberPrice,
  })
  const displayPrice = canViewLuxPlusBenefits && memberPrice > 0 ? memberTotal : publicTotal
  const priceBlockVariation = useOfferTilePriceBlockExperiment(offer, offer?.type === 'bedbank_hotel' ? true : undefined, displayPrice > 0)
  // CRO-7023: do not pair a dated/multi-room price with the cached package's
  // value. Reuse the same discount helper, leaving control's input untouched.
  const valueToDisplay = priceBlockVariation ? priceBlockValue : value
  let hotelOfferDisplayValue: ReturnType<typeof getHotelOfferDisplayedValue> | undefined
  if (!isTourV2) {
    hotelOfferDisplayValue = getHotelOfferDisplayedValue(false, displayPrice, valueToDisplay ?? displayPrice, shouldDisplayValue)
  }

  if (priceBlockVariation) {
    return <div>
      <OfferTilePriceBlockSavingsLabel
        offer={offer}
        price={publicTotal}
        memberPrice={memberTotal}
        offerConfig={offer?.luxPlus}
        variation={priceBlockVariation}
        discountPercentage={hotelOfferDisplayValue?.shouldShowDiscountPercent ? hotelOfferDisplayValue.highestDiscountPercent : undefined}
      />
      <OfferTilePriceBlockCaption
        priceBlockExperimentVariation={priceBlockVariation}
        duration={hotelNights}
        durationLabel={duration}
        saleUnit={offer && 'saleUnit' in offer ? offer.saleUnit : undefined}
        unitCount={roomCount}
      >{durationText}
      </OfferTilePriceBlockCaption>
      <LuxPlusPriceStack
        presentation="price-block-experiment"
        size="S"
        price={Math.ceil(publicTotal)}
        memberPrice={Math.ceil(memberTotal)}
        saleUnit={saleUnit}
        offerConfig={offer?.luxPlus}
      />
      {hotelOfferDisplayValue?.shouldShowValue && !!valueToDisplay && offerType !== 'bedbank_hotel' && <PriceRowValueDiscountWithCaption
        weight="normal"
        colour="light-foreground-alt"
        size="M"
        originalValue={valueToDisplay}
      />}
      {/* This legacy tile does not carry property fees; do not claim they are included. */}
    </div>
  }

  return <div>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    <LuxPlusPriceGuard
      showMemberPrice={canViewLuxPlusBenefits && memberPrice > 0}
      fallbackPrice={<PriceRowPrice
        size="S"
        price={Math.ceil(displayPrice)}
        saleUnit={saleUnit}
        /** @todo Probably YmalPricingProperties needs to include refs to taxes and fees! */
        discountPercentage={!isTourV2 && hotelOfferDisplayValue?.shouldShowDiscountPercent ? hotelOfferDisplayValue.highestDiscountPercent : undefined}
      />}
    >
      <PriceRowLuxPlusPrice
        size="S"
        memberPrice={Math.ceil(displayPrice)}
        saleUnit={saleUnit}
        discountPercentage={!isTourV2 && hotelOfferDisplayValue?.shouldShowDiscountPercent ? hotelOfferDisplayValue.highestDiscountPercent : undefined}
      />
    </LuxPlusPriceGuard>
  </div>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(HotelPriceContent)
